// src/pages/Todo.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { updateCustomer } from "../services/customerService";

/**
 * Todo Page Component
 * Displays customers who have been marked to be added to the to-do list
 */
const Todo = () => {
  const [todoCustomers, setTodoCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch customers marked for to-do list
   */
  useEffect(() => {
    const fetchTodoCustomers = async () => {
      try {
        setLoading(true);
        const customersRef = collection(db, "customer"); // Changed from "customers" to "customer"
        const q = query(customersRef, where("addToTodoList", "==", true));
        const querySnapshot = await getDocs(q);

        const customers = [];
        querySnapshot.forEach((doc) => {
          customers.push({
            ...doc.data(),
            id: doc.id, // Set id AFTER spreading data to ensure it's not overwritten
          });
        });

        setTodoCustomers(customers);
        setError(null);
      } catch (err) {
        console.error("Error fetching to-do customers:", err);
        setError("Failed to load to-do list");
      } finally {
        setLoading(false);
      }
    };

    fetchTodoCustomers();
  }, []);

  /**
   * Get initials from customer name
   */
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Remove customer from To Do list
   */
  const handleRemoveFromTodo = async (customer) => {
    try {
      // Update customer in Firebase
      await updateCustomer(customer.id, {
        ...customer,
        addToTodoList: false,
      });

      // Remove from local state (animates out)
      setTodoCustomers((prev) => prev.filter((c) => c.id !== customer.id));
    } catch (err) {
      console.error("Error removing from to-do list:", err);
      setError("Failed to remove customer from to-do list");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">To Do List</h1>
        <p className="text-gray-600">
          Customers marked for follow-up and action
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {todoCustomers.length === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <svg
                className="mx-auto h-16 w-16 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No customers in to-do list
              </h3>
              <p className="text-gray-600 mb-6">
                Add customers to your to-do list when creating or editing them
              </p>
              <Link
                to="/customers"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
              >
                Go to Customers
              </Link>
            </div>
          ) : (
            /* Customer Cards Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {todoCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
                    <div className="flex items-center">
                      {/* Avatar */}
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {getInitials(customer.name)}
                      </div>
                      {/* Customer Name */}
                      <div className="ml-4 flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {customer.name}
                        </h3>
                        <p className="text-sm text-gray-500">Customer</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 sm:p-6 space-y-3">
                    {/* Phone */}
                    {customer.phone && (
                      <div className="flex items-center text-sm">
                        <svg
                          className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="text-gray-700">{customer.phone}</span>
                      </div>
                    )}

                    {/* Email */}
                    {customer.email && (
                      <div className="flex items-center text-sm">
                        <svg
                          className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-gray-700 truncate">
                          {customer.email}
                        </span>
                      </div>
                    )}

                    {/* Location */}
                    {customer.location && (
                      <div className="flex items-start text-sm">
                        <svg
                          className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-gray-700 line-clamp-2">
                          {customer.location}
                        </span>
                      </div>
                    )}

                    {/* Post Code */}
                    {customer.postCode && (
                      <div className="flex items-center text-sm">
                        <svg
                          className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                          />
                        </svg>
                        <span className="text-gray-700">
                          {customer.postCode}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-2">
                    <Link
                      to={`/customers/${customer.id}/jobs`}
                      className="block w-full text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 font-medium"
                    >
                      View Jobs
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleRemoveFromTodo(customer)}
                      className="flex items-center justify-center w-full px-4 py-2 text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 font-medium"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Remove from To Do
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {todoCustomers.length > 0 && (
            <div className="mt-6 text-center text-sm text-gray-600">
              Showing {todoCustomers.length} customer
              {todoCustomers.length !== 1 ? "s" : ""} in to-do list
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Todo;
